import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { StyleSheet, View, TextInput, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  box: {
    width: 44,
    height: 52,
    borderRadius: 8,
    borderWidth: 2,
    textAlign: 'center',
    fontSize: 20,
    lineHeight: 24,
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
});

const digitOnly = (text) => text.replace(/\D/g, '');

const OtpCodeInput = ({
  length = 6,
  value = '',
  onChange,
  onComplete,
  autoFocus = true,
  isDisabled = false,
  isError = false,
}) => {
  const { colors } = useTheme();
  const inputsRef = useRef([]);
  const [digits, setDigits] = useState(() => {
    const arr = new Array(length).fill('');
    const init = digitOnly(value).slice(0, length).split('');
    init.forEach((d, i) => (arr[i] = d));
    return arr;
  });

  const borderColor = useMemo(() => {
    if (isError) return colors.color_destructive; // red-500
    return colors.text_color_primary; // gray-300
  }, [isError, colors]);

  // Keep internal state in sync with external value changes
  useEffect(() => {
    const v = digitOnly(value).slice(0, length);
    const next = new Array(length).fill('');
    v.split('').forEach((d, i) => (next[i] = d));
    setDigits(next);
  }, [value, length]);

  const focusIndex = useCallback((idx) => {
    const ref = inputsRef.current[idx];
    if (ref && typeof ref.focus === 'function') {
      ref.focus();
    }
  }, []);

  const emitChange = useCallback((arr) => {
    const joined = arr.join('');
    onChange?.(joined);
    if (joined.length === length) {
      onComplete?.(joined);
    }
  }, [onChange, onComplete, length]);

  const handleChangeText = useCallback((text, index) => {
    const incoming = digitOnly(text);

    setDigits((prev) => {
      const next = [...prev];

      // On native (no maxLength=1), typing into a filled box yields
      // old+new digits (e.g. "37"). Strip the previous value so we
      // treat it as a single new keystroke.
      const prevDigit = prev[index] || '';
      const stripped = prevDigit && incoming.startsWith(prevDigit)
        ? incoming.slice(prevDigit.length)
        : incoming;
      const effective = stripped || incoming;

      if (effective.length <= 1) {
        next[index] = effective;
        if (effective && index < length - 1) {
          focusIndex(index + 1);
        }
      } else {
        // Paste / autofill: distribute across boxes starting at index 0
        // when the full code is provided, otherwise start at current index
        const startIdx = effective.length === length ? 0 : index;
        const chars = effective.slice(0, length - startIdx).split('');
        for (let i = 0; i < chars.length; i++) {
          next[startIdx + i] = chars[i];
        }
        const lastFilled = Math.min(startIdx + chars.length - 1, length - 1);
        focusIndex(Math.min(lastFilled + 1, length - 1));
      }

      emitChange(next);
      return next;
    });
  }, [emitChange, focusIndex, length]);

  const handleKeyPress = useCallback((e, index) => {
    if (e?.nativeEvent?.key === 'Backspace') {
      setDigits((prev) => {
        const next = [...prev];
        if (!next[index] && index > 0) {
          // Move back and clear previous
          next[index - 1] = '';
          emitChange(next);
          setTimeout(() => focusIndex(index - 1), 0);
          return next;
        }
        // Clear current if it has a value
        next[index] = '';
        emitChange(next);
        return next;
      });
    }
  }, [emitChange, focusIndex]);

  const handleFocus = useCallback((ref) => {
    // Select existing content for quick overwrite
    try {
      ref?.setNativeProps?.({ selection: { start: 0, end: 1 } });
    } catch {}
  }, []);

  return (
    <View style={styles.container}>
      {new Array(length).fill(0).map((_, idx) => (
        <TextInput
          key={idx}
          ref={(r) => (inputsRef.current[idx] = r)}
          value={digits[idx]}
          onChangeText={(t) => handleChangeText(t, idx)}
          onKeyPress={(e) => handleKeyPress(e, idx)}
          onFocus={() => handleFocus(inputsRef.current[idx])}
          style={[styles.box, { borderColor, color: colors.text_color_primary }]}
          keyboardType={Platform.OS === 'ios' ? 'number-pad' : 'numeric'}
          maxLength={Platform.OS === 'web' ? 1 : length}
          selectionColor={colors.text_color_tertiary}
          textContentType="oneTimeCode"
          autoComplete="one-time-code"
          inputMode="numeric"
          importantForAutofill="yes"
          autoCapitalize="none"
          editable={!isDisabled}
          autoFocus={autoFocus && idx === 0}
          returnKeyType="done"
        />
      ))}
    </View>
  );
};

OtpCodeInput.propTypes = {
  length: PropTypes.number,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onComplete: PropTypes.func,
  autoFocus: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isError: PropTypes.bool,
};

export default React.memo(OtpCodeInput); 